import { esc, html, toast } from '../ui';

/**
 * A document viewer, in the application rather than a new tab.
 *
 * The bytes are fetched with the bearer token and handed to an iframe as an
 * object URL. A plain `<iframe src>` pointing at the API cannot work: the
 * access token lives in memory only, never in a cookie, so a navigation carries
 * no credentials and would 401.
 *
 * The server re-serves the document same-origin for the same reason SkyFile
 * does — EASA send X-Frame-Options: SAMEORIGIN, which stops any other origin
 * embedding their PDF at all, and Content-Disposition: attachment, which pushes
 * the browser to download rather than display it.
 */
export function openPdfViewer(opts: {
  /** API path that returns the bytes, e.g. /aircraft/x/directives/y/pdf */
  path: string;
  title: string;
  subtitle?: string;
  /** The publisher's own link, for opening the original. */
  sourceUrl?: string | null;
  /**
   * For a document whose subject is a list rather than one record — a run of
   * stock labels is sixty ids, which has no business in a query string.
   */
  body?: Record<string, unknown>;
  accessToken: () => string | null;
}): void {
  const existing = document.querySelector('dialog.pdfv');
  existing?.remove();

  const dlg = document.createElement('dialog');
  dlg.className = 'dlg wide pdfv';
  dlg.innerHTML = html`
    <div class="dlg-form">
      <header class="dlg-h">
        <div style="min-width:0;flex:1">
          <h2 style="font-family:var(--mono);font-size:15px">${esc(opts.title)}</h2>
          ${opts.subtitle ? html`<p class="tiny dim">${esc(opts.subtitle)}</p>` : ''}
        </div>
        <div style="display:flex;gap:6px;flex:none">
          <button type="button" class="btn sm" data-tab disabled>Open in a tab</button>
          ${opts.sourceUrl
            ? html`<a class="btn sm" href="${esc(opts.sourceUrl)}" target="_blank" rel="noopener">Source</a>`
            : ''}
          <button type="button" class="x" data-close aria-label="Close">✕</button>
        </div>
      </header>
      <div class="pdfv-body" data-body>
        <p class="muted tiny" style="padding:24px">Loading the document…</p>
      </div>
    </div>
  `;

  const body = dlg.querySelector<HTMLElement>('[data-body]')!;
  const tabButton = dlg.querySelector<HTMLButtonElement>('[data-tab]')!;
  let objectUrl: string | null = null;

  const close = () => {
    // Revoked on the way out: without this every document viewed holds its
    // bytes in memory for as long as the page is open.
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    dlg.close();
    dlg.remove();
  };

  dlg.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', close));
  dlg.addEventListener('cancel', (e) => {
    e.preventDefault();
    close();
  });
  tabButton.addEventListener('click', () => {
    if (objectUrl) window.open(objectUrl, '_blank', 'noopener');
  });

  document.body.appendChild(dlg);
  dlg.showModal();

  void (async () => {
    try {
      const token = opts.accessToken();
      const res = await fetch(`/api${opts.path}`, {
        method: opts.body ? 'POST' : 'GET',
        headers: {
          ...(token ? { authorization: `Bearer ${token}` } : {}),
          ...(opts.body ? { 'content-type': 'application/json' } : {}),
        },
        credentials: 'same-origin',
        body: opts.body ? JSON.stringify(opts.body) : undefined,
      });
      if (!res.ok) {
        const message =
          (await res.json().catch(() => null))?.message ?? `Could not load the document (${res.status}).`;
        body.innerHTML = html`<p class="muted tiny" style="padding:24px">${esc(String(message))}</p>`;
        return;
      }

      objectUrl = URL.createObjectURL(await res.blob());
      body.innerHTML = html`<iframe src="${esc(objectUrl)}" title="${esc(opts.title)}"></iframe>`;
      tabButton.disabled = false;
    } catch (e) {
      body.innerHTML = html`<p class="muted tiny" style="padding:24px">
        ${esc(e instanceof Error ? e.message : 'Could not load the document.')}
      </p>`;
      toast('Could not load that document.', 'error');
    }
  })();
}
